import React, { useEffect, useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
  useWindowDimensions,
  ActivityIndicator,
  Linking,
} from 'react-native';
import { useIsFocused, useRoute } from '@react-navigation/native';
import moment from 'moment';
import { Ionicons, AntDesign, MaterialCommunityIcons, Feather } from '@expo/vector-icons';

import Urls from '../../services/Urls';
import Text from '../../components/text';
import ajax from '../../helpers/ajaxHelper';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import Knob from '../../components/knob';
import { SHADOWS } from '../../constants/theme';
import { usePropertyRouteParams } from './sharedComponents';
import { showErrorToast } from '../tenants/tenantOnboarding/helpers';

/* ─── Helpers for Certificate / License / Requirement status badges & dates ─── */
const getItemData = (data) => (Array.isArray(data) ? data[0] : data);

const getCertStatus = (cer) => {
  const item = getItemData(cer);
  if (item && item.expiry_date) {
    if (moment(item.expiry_date).isBefore(moment())) {
      return { label: 'Expired', color: '#DC2626', bg: '#FEE2E2' };
    }
    return { label: 'Valid', color: '#059669', bg: '#D1FAE5' };
  }
  return { label: 'Action Required', color: '#DC2626', bg: '#FEE2E2' };
};

const getLicStatus = (lic) => {
  const item = getItemData(lic);
  if (item && (item.status === 'Yes' || item.status === 'Not Applicable' || item.expiry_date)) {
    if (item.expiry_date && moment(item.expiry_date).isBefore(moment())) {
      return { label: 'Expired', color: '#DC2626', bg: '#FEE2E2' };
    }
    return { label: 'Valid', color: '#059669', bg: '#D1FAE5' };
  }
  return { label: 'Not Provided', color: '#D97706', bg: '#FEF3C7' };
};

const getReqStatus = (isAvailable, testDate) => {
  if (isAvailable) {
    if (testDate) {
      const addOneYear = moment(testDate).add(1, 'y');
      if (addOneYear.isAfter(moment())) {
        return { label: 'Verified', color: '#059669', bg: '#D1FAE5' };
      }
    }
    return { label: 'Check Due', color: '#D97706', bg: '#FEF3C7' };
  }
  return { label: 'Missing', color: '#DC2626', bg: '#FEE2E2' };
};

const getCertificateDate = (cer) => {
  const item = getItemData(cer);
  if (item && item.expiry_date) {
    const m = moment(item.expiry_date);
    return m.isValid() ? m.format('DD/MM/YYYY') : null;
  }
  return null;
};

const getCertificateFileUrl = (cer) => {
  if (!cer) return null;
  const item = getItemData(cer);
  if (!item) return null;

  if (item.document_url) return item.document_url;
  if (item.openbrixName) return item.openbrixName;
  if (item.url) return item.url;
  if (item.file_url) return item.file_url;
  if (item.certificate_url) return item.certificate_url;
  if (item.management_link) return item.management_link;

  const files = item.files || cer.files;
  if (Array.isArray(files) && files.length > 0) {
    const valid = files.find(
      (f) => !f?.is_deleted && (f?.document_url || f?.openbrixName || f?.url || f?.file_url)
    );
    if (valid) {
      return valid.document_url || valid.openbrixName || valid.url || valid.file_url;
    }
    const first = files[0];
    if (typeof first === 'string') return first;
    return first?.document_url || first?.openbrixName || first?.url || first?.file_url || null;
  }

  const buyed = item.buyed || cer.buyed;
  if (Array.isArray(buyed) && buyed.length > 0) {
    const b = buyed[0];
    return b?.management_link || b?.document_url || b?.url || null;
  }

  return null;
};

const getDetectorDate = (date) => {
  if (date) {
    const m = moment(date);
    return m.isValid() ? m.format('DD/MM/YYYY') : null;
  }
  return null;
};

const getRoomDetails = (quantity) => [
  { key: 'Bedrooms', value: quantity?.Bedrooms || 0, icon: 'bed-outline' },
  { key: 'Bathrooms', value: quantity?.Bathrooms || 0, icon: 'water-outline' },
  { key: 'Kitchens', value: quantity?.Kitchens || 0, icon: 'restaurant-outline' },
  { key: 'Living Room', value: quantity?.['Living Room'] || 0, icon: 'tv-outline' },
  { key: 'Dining Room', value: quantity?.['Dining Room'] || 0, icon: 'cafe-outline' },
  { key: 'Office', value: quantity?.Office || 0, icon: 'briefcase-outline' },
  { key: 'Utility Area', value: quantity?.['Utility Area'] || 0, icon: 'build-outline' },
  { key: 'Basement', value: quantity?.Basement || 0, icon: 'layers-outline' },
  { key: 'Lift', value: quantity?.Lift || 0, icon: 'swap-vertical-outline' },
  { key: 'Conservatory', value: quantity?.Conservatory || 0, icon: 'leaf-outline' },
  { key: 'Other rooms', value: quantity?.['Other rooms'] || 0, icon: 'grid-outline' },
  { key: 'Garden', value: quantity?.Garden || 0, icon: 'flower-outline' },
  { key: 'Parking Space', value: quantity?.['Parking Space'] || 0, icon: 'car-outline' },
  { key: 'Other', value: quantity?.Other || 0, icon: 'ellipsis-horizontal-circle-outline' },
  { key: 'Garage', value: quantity?.Garage || 0, icon: 'construct-outline' },
];

/* ─── Status Badge Component ───────────────────────────────────── */
const StatusPill = ({ label, color, bg }) => (
  <View style={[pill.wrap, { backgroundColor: bg }]}>
    <View style={[pill.dot, { backgroundColor: color }]} />
    <Text style={[pill.txt, { color }]}>{label}</Text>
  </View>
);

const pill = StyleSheet.create({
  wrap: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  txt: { fontSize: 11, fontWeight: '700' },
});

/* ─── Section Card Wrapper (Collapsible Accordion in Mobile) ─── */
const SectionCard = ({ title, onEdit, children, isMobile, icon, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // In desktop mode, sections are always open
  const expanded = !isMobile || isOpen;

  const toggleOpen = () => {
    if (isMobile) {
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <View style={[sec.card, isMobile && sec.cardMobile, isMobile && !expanded && sec.cardCollapsed]}>
      <View style={[sec.hdr, isMobile && sec.hdrMobile, isMobile && !expanded && sec.hdrCollapsed]}>
        <TouchableOpacity
          activeOpacity={isMobile ? 0.7 : 1}
          onPress={toggleOpen}
          disabled={!isMobile}
          style={sec.titleWrap}
        >
          {icon && (
            <View style={sec.iconBox}>
              <Ionicons name={icon} size={18} color="#6C2BD9" />
            </View>
          )}
          <Text style={sec.title}>{title}</Text>
        </TouchableOpacity>

        <View style={sec.rightActions}>
          {onEdit && (
            <TouchableOpacity style={sec.editBtn} onPress={onEdit} activeOpacity={0.8}>
              <Feather name="edit-2" size={13} color="#6C2BD9" />
              <Text style={sec.editLink}>Edit</Text>
            </TouchableOpacity>
          )}

          {isMobile && (
            <TouchableOpacity
              style={sec.chevronBox}
              onPress={toggleOpen}
              activeOpacity={0.7}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons
                name={isOpen ? 'chevron-up' : 'chevron-down'}
                size={18}
                color="#6C2BD9"
              />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {expanded && children}
    </View>
  );
};

const sec = StyleSheet.create({
  card: { backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, borderWidth: 1, borderColor: '#F3F4F6', ...SHADOWS.sm },
  cardMobile: { padding: 14, borderRadius: 14, marginBottom: 12 },
  cardCollapsed: { paddingBottom: 14 },
  hdr: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  hdrMobile: { marginBottom: 14 },
  hdrCollapsed: { marginBottom: 0 },
  titleWrap: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 },
  rightActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  chevronBox: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBox: { width: 34, height: 34, borderRadius: 10, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: 16, fontWeight: '700', color: '#111827' },
  editBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#F0E8FF', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20 },
  editLink: { fontSize: 12, fontWeight: '700', color: '#6C2BD9' },
});

/* ─── Main Property Info Screen Component ──────────────────────── */
const PropertyInfo = ({ navigation }) => {
  const route = useRoute();
  const focused = useIsFocused();
  const routeParams = usePropertyRouteParams();
  const activePropertyId = route?.params?.id || routeParams.id;
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    id: activePropertyId || '',
    name: routeParams.name || 'Property',
    address: '',
    percentage: 0,
    quantity: {},
    images: [],
    floor_plan_images: [],
    propertyUtilities: {},
    certificates: {},
    licenses: {},
    is_smoke_detector_available_on_every_floor: false,
    is_carbon_monoxide_detector_available_in_every_room: false,
    smoke_detector_testing_date: null,
    carbon_monoxide_detector_testing_date: null,
  });

  useEffect(() => {
    if (activePropertyId) {
      _getDetails(activePropertyId);
    }
  }, [activePropertyId, focused]);

  const _getDetails = (id) => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      ajax.get(`${Urls.Properties}/${id}`).catch((err) => ({ data: null })),
      ajax.get(`${Urls.Certificates}/${id}`).catch((err) => ({ data: null })),
    ])
      .then(([propRes, certRes]) => {
        const p = propRes?.data?.payload || propRes?.data?.data?.payload || propRes?.data;
        const allCerts = certRes?.data?.data?.allPropertyCertificates || certRes?.data?.payload?.allPropertyCertificates || {};
        const buyedCerts = certRes?.data?.data?.buyedCertificates || certRes?.data?.payload?.buyedCertificates || {};

        if (p) {
          const fullAddr = [p.house_name_num, p.address_line1, p.city, p.postcode, p.country]
            .filter(Boolean)
            .join(', ');

          const pCerts = p.certificates || {};

          const mergeCert = (pItem, cItem, bItem) => {
            const base = (Array.isArray(pItem) ? pItem[0] : pItem) || {};
            const certObj = (Array.isArray(cItem) ? cItem[0] : cItem) || {};
            const files = (certObj.files && certObj.files.length > 0)
              ? certObj.files
              : (base.files && base.files.length > 0)
                ? base.files
                : [];
            return {
              ...base,
              ...certObj,
              files,
              expiry_date: certObj.expiry_date || base.expiry_date || '',
              status: certObj.status || base.status || '',
              buyed: bItem || base.buyed || null,
            };
          };

          const mergedCertificates = {
            ...pCerts,
            gas_certificate: mergeCert(pCerts.gas_certificate, allCerts.gas_certificate, buyedCerts.gas_certificate),
            eicr_certificate: mergeCert(pCerts.eicr_certificate, allCerts.eicr_certificate, buyedCerts.eicr_certificate),
            epc_certificate: mergeCert(pCerts.epc_certificate, allCerts.epc_certificate, buyedCerts.epc_certificate),
            other_certificate: mergeCert(pCerts.other_certificate, allCerts.other_certificate, null),
          };

          setState({
            id: p.id || id,
            name: p.name || 'Property',
            address: fullAddr || p.address_line1 || 'No address provided',
            percentage: typeof p.percentage === 'number' ? p.percentage : 0,
            quantity: p.quantity || {},
            images: p.images || [],
            floor_plan_images: p.floor_plan_images || [],
            propertyUtilities: p.propertyUtilities || p.utilities || {},
            certificates: mergedCertificates,
            licenses: p.licenses || {},
            is_smoke_detector_available_on_every_floor: Boolean(p.is_smoke_detector_available_on_every_floor),
            is_carbon_monoxide_detector_available_in_every_room: Boolean(p.is_carbon_monoxide_detector_available_in_every_room),
            smoke_detector_testing_date: p.smoke_detector_testing_date || null,
            carbon_monoxide_detector_testing_date: p.carbon_monoxide_detector_testing_date || null,
          });
        }
      })
      .catch((err) => console.log('error fetching property details in PropertyInfo', err))
      .finally(() => setLoading(false));
  };

  /* ── View Certificate / License in New Window ── */
  const handleViewCertificate = (cerData, label) => {
    const url = getCertificateFileUrl(cerData);
    if (url) {
      if (typeof window !== 'undefined' && window.open) {
        window.open(url, '_blank');
      } else if (Linking && Linking.openURL) {
        Linking.openURL(url);
      }
    } else {
      showErrorToast(`No ${label || 'certificate'} document uploaded yet`);
    }
  };

  /* ── Header Hero Card ── */
  const renderHeroHeader = () => (
    <View style={[hdr.card, isMobile && hdr.cardMobile]}>
      <View style={hdr.leftCol}>
        <View style={hdr.iconWrap}>
          <Ionicons name="home" size={30} color="#6C2BD9" />
        </View>
        <View style={hdr.textWrap}>
          <View style={hdr.titleRow}>
            <Text style={hdr.title}>{state.name}</Text>
            <View style={hdr.typeBadge}>
              <Text style={hdr.typeBadgeText}>Property Overview</Text>
            </View>
          </View>
          <View style={hdr.addressRow}>
            <Ionicons name="location-outline" size={14} color="#6B7280" />
            <Text style={hdr.subtitle} numberOfLines={2}>{state.address}</Text>
          </View>
        </View>
      </View>
      <View style={hdr.knobWrap}>
        <Knob
          size={76}
          strokeWidth={8}
          progressPercent={Math.round(state.percentage || 0)}
          text={'Complete'}
          textColor={'#6C2BD9'}
          pgColor={'#6C2BD9'}
        />
      </View>
    </View>
  );

  /* ── Utility Card Renderer ── */
  const renderUtilityItem = (title, isAvailable, companyName, iconName) => (
    <View style={[ut.cardItem, isMobile && ut.cardItemMobile]}>
      <View style={ut.cardTop}>
        <View style={ut.titleRow}>
          <View style={ut.iconCircle}>
            <Ionicons name={iconName || 'flash-outline'} size={15} color="#6C2BD9" />
          </View>
          <Text style={ut.title} numberOfLines={1}>{title}</Text>
        </View>
        <StatusPill
          label={isAvailable ? 'Yes' : 'No'}
          color={isAvailable ? '#059669' : '#6B7280'}
          bg={isAvailable ? '#D1FAE5' : '#F3F4F6'}
        />
      </View>
      <View style={ut.divider} />
      <View style={ut.cardBottom}>
        <Text style={ut.companyLabel}>Company Name</Text>
        <Text style={ut.companyName} numberOfLines={1}>{companyName || '—'}</Text>
      </View>
    </View>
  );

  /* ── Certificate Row Renderer ── */
  const renderCertRow = (label, cerData) => {
    const s = getCertStatus(cerData);
    const fileUrl = getCertificateFileUrl(cerData);
    return (
      <View style={[rw.parentRow, isMobile && rw.parentRowMobile]}>
        <View style={[rw.titleWrap, { width: isMobile ? '100%' : '35%' }]}>
          <Text style={rw.titleLabel}>{label}</Text>
          <StatusPill label={s.label} color={s.color} bg={s.bg} />
        </View>
        <View style={[rw.subRow, { width: isMobile ? '100%' : '65%' }]}>
          <Text style={rw.dateLabel}>Expiry Date</Text>
          <View style={rw.dateAndActionWrap}>
            <View style={{ width: isMobile ? 140 : 180 }}>
              <TextInputComponent
                isDate
                disabled
                onChangeText={() => {}}
                date={getCertificateDate(cerData)}
              />
            </View>
            <TouchableOpacity
              style={[rw.eyeBtn, fileUrl ? rw.eyeBtnActive : rw.eyeBtnDisabled]}
              onPress={() => handleViewCertificate(cerData, label)}
              activeOpacity={0.7}
              title={fileUrl ? 'View Certificate in new window' : 'No document uploaded'}
            >
              <Feather
                name="eye"
                size={16}
                color={fileUrl ? '#6C2BD9' : '#9CA3AF'}
              />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  /* ── License Row Renderer ── */
  const renderLicRow = (label, licData) => {
    const s = getLicStatus(licData);
    const fileUrl = getCertificateFileUrl(licData);
    return (
      <View style={[rw.parentRow, isMobile && rw.parentRowMobile]}>
        <View style={[rw.titleWrap, { width: isMobile ? '100%' : '35%' }]}>
          <Text style={rw.titleLabel}>{label}</Text>
          <StatusPill label={s.label} color={s.color} bg={s.bg} />
        </View>
        <View style={[rw.subRow, { width: isMobile ? '100%' : '65%' }]}>
          <Text style={rw.dateLabel}>Expiry Date</Text>
          <View style={rw.dateAndActionWrap}>
            <View style={{ width: isMobile ? 140 : 180 }}>
              <TextInputComponent
                isDate
                disabled
                onChangeText={() => {}}
                date={getCertificateDate(licData)}
              />
            </View>
            <TouchableOpacity
              style={[rw.eyeBtn, fileUrl ? rw.eyeBtnActive : rw.eyeBtnDisabled]}
              onPress={() => handleViewCertificate(licData, label)}
              activeOpacity={0.7}
              title={fileUrl ? 'View License in new window' : 'No document uploaded'}
            >
              <Feather
                name="eye"
                size={16}
                color={fileUrl ? '#6C2BD9' : '#9CA3AF'}
              />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  /* ── Requirement Row Renderer ── */
  const renderReqRow = (label, isAvailable, testDate) => {
    const s = getReqStatus(isAvailable, testDate);
    return (
      <View style={[rw.parentRow, isMobile && rw.parentRowMobile]}>
        <View style={[rw.titleWrap, { width: isMobile ? '100%' : '35%' }]}>
          <Text style={rw.titleLabel}>{label}</Text>
          <StatusPill label={s.label} color={s.color} bg={s.bg} />
        </View>
        <View style={[rw.subRow, { width: isMobile ? '100%' : '65%' }]}>
          <Text style={rw.dateLabel}>Last Check Date</Text>
          <View style={{ width: isMobile ? '55%' : '50%' }}>
            <TextInputComponent
              isDate
              disabled
              onChangeText={() => {}}
              date={getDetectorDate(testDate)}
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={ws.root}>
      {loading && (
        <View style={ws.loaderOverlay}>
          <ActivityIndicator size="large" color="#6C2BD9" />
        </View>
      )}

      {/* Breadcrumb Header Bar */}
      <View style={[ws.breadcrumbBar, isMobile && { paddingHorizontal: 12, paddingVertical: 10 }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={ws.backBtn} activeOpacity={0.8}>
          <AntDesign name="left" size={12} color="#6C2BD9" />
          <Text style={ws.backText}>Back</Text>
        </TouchableOpacity>
        <Text style={ws.breadSep}>Properties</Text>
        <Text style={ws.breadSep}>›</Text>
        <Text style={[ws.breadSep, { color: '#6C2BD9', fontWeight: '600' }]}>{state.name}</Text>
        <Text style={ws.breadSep}>›</Text>
        <Text style={[ws.breadSep, { color: '#111827', fontWeight: '600' }]}>Property Details</Text>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[ws.scroll, isMobile && { paddingHorizontal: 12 }]}
        showsVerticalScrollIndicator={false}
      >
        {/* Top Header Card */}
        {renderHeroHeader()}

        {/* 1. Property Details Section */}
        <SectionCard
          title="Property Details"
          icon="home-outline"
          isMobile={isMobile}
          onEdit={() => navigation.navigate('PropertyDetails', { id: state.id })}
        >
          <View style={rm.gridWrap}>
            {getRoomDetails(state.quantity).map((item) => (
              <View key={item.key} style={[rm.tile, { width: isMobile ? '48%' : '31.5%' }]}>
                <View style={rm.tileHeader}>
                  <Ionicons name={item.icon} size={16} color="#6C2BD9" />
                  <Text style={rm.tileTitle} numberOfLines={1}>{item.key}</Text>
                </View>
                <Text style={rm.tileCount}>{item.value}</Text>
              </View>
            ))}
          </View>
        </SectionCard>

        {/* 2. Property Photos Section */}
        <SectionCard
          title="Property Photos"
          icon="images-outline"
          isMobile={isMobile}
          onEdit={() => navigation.navigate('PropertyDetails', { id: state.id })}
        >
          {state.images && state.images.length > 0 ? (
            <View style={img.gridWrap}>
              {state.images.map((data, index) => (
                <View key={index} style={img.card}>
                  <Image source={{ uri: data.openbrixName || data.document_url || data.url }} style={img.photo} />
                </View>
              ))}
            </View>
          ) : (
            <View style={img.emptyBox}>
              <Ionicons name="camera-outline" size={28} color="#9CA3AF" />
              <Text style={img.emptyText}>No Photos Uploaded Yet</Text>
            </View>
          )}
        </SectionCard>

        {/* 3. Property Floor Plan Section */}
        <SectionCard
          title="Property Floor Plan"
          icon="document-text-outline"
          isMobile={isMobile}
          onEdit={() => navigation.navigate('PropertyDetails', { id: state.id })}
        >
          {state.floor_plan_images && state.floor_plan_images.length > 0 ? (
            <View style={img.gridWrap}>
              {state.floor_plan_images.map((data, index) => {
                const uri = data.openbrixName || data.document_url || data.url;
                const isPdf = uri?.toLowerCase().endsWith('.pdf');
                return (
                  <View key={index} style={img.card}>
                    {isPdf ? (
                      <View style={[img.photo, { justifyContent: 'center', alignItems: 'center', backgroundColor: '#e0e0e0' }]}>
                        <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#6D08C0' }}>PDF</Text>
                      </View>
                    ) : (
                      <Image source={{ uri }} style={img.photo} />
                    )}
                  </View>
                );
              })}
            </View>
          ) : (
            <View style={img.emptyBox}>
              <Ionicons name="map-outline" size={28} color="#9CA3AF" />
              <Text style={img.emptyText}>No Floor Plan Uploaded Yet</Text>
            </View>
          )}
        </SectionCard>

        {/* 4. Utilities Section */}
        <SectionCard
          title="Utilities"
          icon="flash-outline"
          isMobile={isMobile}
          onEdit={() => navigation.navigate('Utilities', { id: state.id, key: 'utility_details_status' })}
        >
          <View style={ut.gridWrap}>
            {renderUtilityItem(
              'Ground Rent Info',
              state.propertyUtilities?.is_ground_rent_info_available,
              state.propertyUtilities?.property_utility_types?.ground_rent_info?.company_name,
              'business-outline'
            )}
            {renderUtilityItem(
              'Service Charge Info',
              state.propertyUtilities?.is_service_charge_info_available,
              state.propertyUtilities?.property_utility_types?.service_charge_info?.company_name,
              'card-outline'
            )}
            {renderUtilityItem(
              'Water Account Info',
              state.propertyUtilities?.is_water_account_info_available,
              state.propertyUtilities?.property_utility_types?.water_account_info?.company_name,
              'water-outline'
            )}
            {renderUtilityItem(
              'Gas Account Info',
              state.propertyUtilities?.is_gas_account_info_available,
              state.propertyUtilities?.property_utility_types?.gas_account_info?.company_name,
              'flame-outline'
            )}
            {renderUtilityItem(
              'Electric Account Info',
              state.propertyUtilities?.is_electric_account_info_available,
              state.propertyUtilities?.property_utility_types?.electric_account_info?.company_name,
              'flash-outline'
            )}
          </View>
        </SectionCard>

        {/* 5. Certificate Section */}
        <SectionCard
          title="Certificate"
          icon="shield-checkmark-outline"
          isMobile={isMobile}
          onEdit={() =>
            navigation.navigate('Certification', {
              id: state.id,
              key: 'certificate_details_status',
            })
          }
        >
          {renderCertRow('Gas Certificate', state.certificates?.gas_certificate)}
          {renderCertRow('EICR Certificate', state.certificates?.eicr_certificate)}
          {renderCertRow('EPC Certificate', state.certificates?.epc_certificate)}
        </SectionCard>

        {/* 6. License Section */}
        <SectionCard
          title="License"
          icon="ribbon-outline"
          isMobile={isMobile}
          onEdit={() =>
            navigation.navigate('Licensing', {
              id: state.id,
              key: 'license_details_status',
            })
          }
        >
          {renderLicRow('HMO License', state.licenses?.hmo_license)}
          {renderLicRow('Selective Licence', state.licenses?.selective_license)}
        </SectionCard>

        {/* 7. Habitation Requirements Section */}
        <SectionCard
          title="Habitation Requirements"
          icon="checkmark-circle-outline"
          isMobile={isMobile}
          onEdit={() =>
            navigation.navigate('Requirements', {
              id: state.id,
              key: 'property_requirements_status',
            })
          }
        >
          {renderReqRow(
            'Smoke Detector',
            state.is_smoke_detector_available_on_every_floor,
            state.smoke_detector_testing_date
          )}
          {renderReqRow(
            'Carbon M Detector',
            state.is_carbon_monoxide_detector_available_in_every_room,
            state.carbon_monoxide_detector_testing_date
          )}
        </SectionCard>

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

/* ─── Premium Modern Styles ─────────────────────────────────────── */
const hdr = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#F3F4F6',
    ...SHADOWS.md,
  },
  cardMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    padding: 16,
  },
  leftCol: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    flex: 1,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: '#F0E8FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textWrap: { flex: 1 },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  title: { fontSize: 20, fontWeight: '700', color: '#111827' },
  typeBadge: { backgroundColor: '#F0E8FF', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 2 },
  typeBadgeText: { fontSize: 11, fontWeight: '700', color: '#6C2BD9' },
  addressRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 6 },
  subtitle: { fontSize: 13, color: '#6B7280', flex: 1 },
  knobWrap: { alignItems: 'center', justifyContent: 'center' },
});

const rm = StyleSheet.create({
  gridWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginVertical: 4 },
  tile: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#F3F4F6',
    alignItems: 'center',
  },
  tileHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  tileTitle: { fontSize: 13, fontWeight: '600', color: '#374151' },
  tileCount: { fontSize: 18, fontWeight: '700', color: '#6C2BD9' },
});

const img = StyleSheet.create({
  gridWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 4 },
  card: { width: 180, height: 115, borderRadius: 12, overflow: 'hidden', backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: '#E5E7EB' },
  photo: { width: '100%', height: '100%', resizeMode: 'cover' },
  emptyBox: { alignItems: 'center', justifyContent: 'center', padding: 24, backgroundColor: '#F9FAFB', borderRadius: 12, borderStyle: 'dashed', borderWidth: 1, borderColor: '#D1D5DB' },
  emptyText: { fontSize: 13, color: '#9CA3AF', fontWeight: '600', marginTop: 6 },
});

const ut = StyleSheet.create({
  gridWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 4 },
  cardItem: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#F3F4F6',
    flex: 1,
    minWidth: 210,
  },
  cardItemMobile: { minWidth: '100%' },
  cardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 },
  iconCircle: { width: 28, height: 28, borderRadius: 8, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: 13, fontWeight: '700', color: '#111827', flex: 1 },
  divider: { height: 1, backgroundColor: '#E5E7EB', marginVertical: 10 },
  cardBottom: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  companyLabel: { fontSize: 12, color: '#6B7280' },
  companyName: { fontSize: 13, fontWeight: '700', color: '#111827' },
});

const rw = StyleSheet.create({
  parentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent:'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F9FAFB',
  },
  parentRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 12,
  },
  titleWrap: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  titleLabel: { fontSize: 14, fontWeight: '600', color: '#111827' },
  subRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dateAndActionWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  eyeBtn: {
    width: 38,
    height: 38,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  eyeBtnActive: {
    backgroundColor: '#F0E8FF',
    borderColor: '#DDD6FE',
  },
  eyeBtnDisabled: {
    backgroundColor: '#F9FAFB',
    borderColor: '#E5E7EB',
  },
  dateLabel: { fontSize: 13, fontWeight: '600', color: '#6B7280', marginRight: 12 },
});

const ws = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },
  breadcrumbBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 24,
    paddingVertical: 14,
    flexWrap: 'wrap',
  },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 },
  backText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  breadSep: { fontSize: 13, color: '#9CA3AF' },
  scroll: { paddingHorizontal: 24, paddingVertical: 8, paddingBottom: 60 },
  loaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.7)',
    zIndex: 999,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default drawerHoc({
  Component: PropertyInfo,
  showBackButton: true,
  noScroll: true,
});
